import { useState } from 'react';
import { AlertTriangle, Check, ListChecks, Sparkles } from 'lucide-react';

/**
 * ChapterSummaryView — the readable chapter summary.
 *
 * Shared by the Summary page and by the "I explain the chapter" step of a
 * guided session, so a student never sees two different summaries of the same
 * chapter. `compact` drops the sticky contents rail for in-panel use.
 */
export function ChapterSummaryView({ summary, compact = false }) {
  const sections = summary?.sections || [];
  const [active, setActive] = useState('');

  if (!summary) return null;

  const jump = (id) => {
    setActive(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const body = (
    <div className={compact ? 'space-y-4' : 'space-y-7'} data-testid="chapter-summary-body">
      <div>
        {summary.hook && (
          <p className="font-body text-sm sm:text-base text-slate-700 leading-relaxed" data-testid="summary-hook">
            {summary.hook}
          </p>
        )}
        {(summary.objectives || []).length > 0 && (
          <div className="mt-3">
            <p className="font-heading text-xs uppercase tracking-widest text-slate-400">By the end you can</p>
            <ul className="mt-2 space-y-1.5">
              {summary.objectives.map((o, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-[13px] sm:text-sm text-slate-700">
                  <ListChecks className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" /> {o}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {sections.map((s, i) => (
        <section key={i} id={`summary-section-${i}`} data-testid={`summary-section-${i}`}>
          <h2 className="font-display text-lg sm:text-2xl font-bold text-slate-900 tracking-tight">{s.heading}</h2>
          {(s.subheadings || []).length > 0 && (
            <p className="mt-1 font-body text-[11px] text-slate-400">{s.subheadings.join(' · ')}</p>
          )}
          <ul className="mt-3 space-y-2">
            {(s.bullets || []).map((b, bi) => (
              <li key={bi} className="flex items-start gap-2 font-body text-[13px] sm:text-sm text-slate-700 leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" /> {b}
              </li>
            ))}
          </ul>
          {(s.key_terms || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {s.key_terms.map((t, ti) => (
                <span key={ti} title={t.meaning}
                  className="text-[11px] font-heading px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  {t.term} — <span className="font-body text-slate-500">{t.meaning}</span>
                </span>
              ))}
            </div>
          )}
        </section>
      ))}

      {(summary.exam_watchouts || []).length > 0 && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4" data-testid="summary-watchouts">
          <p className="font-heading text-sm font-bold text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Exam watch-outs
          </p>
          <ul className="mt-2 space-y-1.5">
            {summary.exam_watchouts.map((w, i) => (
              <li key={i} className="font-body text-[13px] text-amber-900/90">• {w}</li>
            ))}
          </ul>
        </section>
      )}

      {(summary.recap || []).length > 0 && (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4" data-testid="summary-recap">
          <p className="font-heading text-sm font-bold text-emerald-800 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> The whole chapter in one breath
          </p>
          <ul className="mt-2 space-y-1.5">
            {summary.recap.map((r, i) => (
              <li key={i} className="flex items-start gap-2 font-body text-[13px] text-emerald-900/90">
                <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {r}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );

  if (compact) return body;

  return (
    <div className="grid lg:grid-cols-[220px_1fr] gap-8">
      <nav className="hidden lg:block" data-testid="summary-contents">
        <div className="sticky top-6 space-y-1">
          <p className="font-heading text-[11px] uppercase tracking-widest text-slate-400 mb-2">Contents</p>
          {sections.map((s, i) => (
            <button key={i} onClick={() => jump(`summary-section-${i}`)}
              data-testid={`summary-toc-${i}`}
              className={`w-full text-left text-[12px] font-body px-3 py-2 rounded-xl transition-colors ${
                active === `summary-section-${i}` ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}>
              <span className="font-heading text-slate-400 mr-1.5">{i + 1}.</span>{s.heading}
            </button>
          ))}
        </div>
      </nav>
      {body}
    </div>
  );
}

export default ChapterSummaryView;
